import { useEffect, useRef, useState } from 'react';
import {
  api,
  problemsIn,
  type ImportChoices,
  type ImportColumn,
  type ImportFormat,
  type ImportPreview,
  type ImportResult,
} from '../lib/api';
import { Gate, ProgramHeader, useProgram, type Program } from './Residency';

/**
 * Uploading a year (D48).
 *
 * The import has been right and unreachable since it was written: it refuses
 * whole on any problem, applies in one transaction, and matches everything on
 * its natural key so a corrected file moves what moved. All of which was true
 * only for somebody with a terminal, which a program director is not.
 *
 * This is the front door. What it is careful about:
 *
 *   - **Nothing is applied without a preview.** The Upload button does not
 *     exist until a preview has come back clean. The preview and the import
 *     run the same functions on the server, so every number on this screen is
 *     the number that happens.
 *   - **A refusal is the file's fault, said in the file's terms.** Problems
 *     come back as sentences naming residents and blocks, and they are
 *     printed as they are rather than summarised — a program fixing a
 *     spreadsheet needs the row, not a count.
 *   - **Replace is asked for by name.** Merge speaks only for what is in the
 *     file, which is what makes uploading a two-resident correction safe.
 *     Replace clears the rest of the year, so it says how many residents
 *     would leave the roster before it is allowed to happen.
 *   - **The accepted columns are printed from the reader's own table**
 *     (GET /residency/import/format), so the help cannot drift away from what
 *     the server actually matches.
 */

type Stage =
  | { state: 'idle' }
  | { state: 'reading' }
  | { state: 'previewing' }
  | { state: 'previewed'; preview: ImportPreview }
  | { state: 'refused'; problems: string[]; message: string }
  | { state: 'importing'; preview: ImportPreview }
  | { state: 'done'; result: ImportResult };

export function ResidencyImport({ clients, scheduleId, year }: { clients: string[] | null; scheduleId: string | null; year: string | null }) {
  const loaded = useProgram(clients, scheduleId, year);
  if (loaded.state !== 'ready') return <Gate loaded={loaded} />;
  return (
    <section className="residency import">
      <ProgramHeader program={loaded.program} path="/admin/residency/import" />
      <ImportBody program={loaded.program} />
    </section>
  );
}

function ImportBody({ program }: { program: Program }) {
  const { clientId, schedule } = program;
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [choices, setChoices] = useState<ImportChoices>({ mode: 'merge', createUnknown: false });
  const [stage, setStage] = useState<Stage>({ state: 'idle' });
  const input = useRef<HTMLInputElement>(null);

  // A preview is only true of the choices it was run with. Changing either
  // switch throws it away rather than leaving a stale set of numbers above an
  // Upload button that would do something else.
  const reset = () => setStage({ state: 'idle' });

  const choose = async (chosen: File | null) => {
    reset();
    if (!chosen) {
      setFile(null);
      return;
    }
    setStage({ state: 'reading' });
    try {
      const text = await chosen.text();
      setFile({ name: chosen.name, text });
      setStage({ state: 'idle' });
    } catch (e) {
      setFile(null);
      setStage({ state: 'refused', message: `That file could not be opened (${(e as Error).message}).`, problems: [] });
    }
  };

  const failed = (e: unknown, fallback: string) => {
    const problems = problemsIn(e);
    setStage({ state: 'refused', message: (e as Error)?.message || fallback, problems });
  };

  const runPreview = async () => {
    if (!file) return;
    setStage({ state: 'previewing' });
    try {
      setStage({ state: 'previewed', preview: await api.residencyImportPreview(clientId, schedule.id, file.text, choices) });
    } catch (e) {
      failed(e, 'The preview failed.');
    }
  };

  const runImport = async (preview: ImportPreview) => {
    if (!file) return;
    setStage({ state: 'importing', preview });
    try {
      setStage({ state: 'done', result: await api.residencyImport(clientId, schedule.id, file.text, choices) });
    } catch (e) {
      failed(e, 'The upload failed.');
    }
  };

  const startOver = () => {
    setFile(null);
    if (input.current) input.current.value = '';
    reset();
  };

  return (
    <>
      <div className="card">
        <h2>Upload a year</h2>
        <p>
          A program's block schedule is the program's own document. It arrives here whole — every resident, every block — and
          is corrected by uploading a corrected file, so there is never a second version of it living on this screen.
        </p>
        <p className="muted small">
          Nothing is written until you have seen exactly what would change and said so. An upload with anything wrong in it is
          refused entirely: there is no half-loaded year.
        </p>

        <label className="field">
          <span>The file</span>
          <input
            ref={input}
            type="file"
            accept=".csv,.tsv,.txt,.json,text/csv,application/json"
            onChange={(e) => choose(e.target.files?.[0] ?? null)}
            disabled={stage.state === 'importing' || stage.state === 'done'}
          />
        </label>
        {file && (
          <p className="small">
            <strong>{file.name}</strong> <span className="muted">· {sizeOf(file.text)}</span>{' '}
            <button className="linklike" type="button" onClick={startOver}>choose another</button>
          </p>
        )}

        <Choices choices={choices} onChange={(next) => { setChoices(next); reset(); }} locked={stage.state === 'importing' || stage.state === 'done'} />

        {stage.state !== 'done' && (
          <p>
            <button
              className="primary"
              type="button"
              disabled={!file || stage.state === 'reading' || stage.state === 'previewing' || stage.state === 'importing'}
              onClick={runPreview}
            >
              {stage.state === 'previewing' ? 'Checking…' : stage.state === 'previewed' ? 'Check it again' : 'Check this file'}
            </button>
          </p>
        )}
      </div>

      {stage.state === 'refused' && <Refused message={stage.message} problems={stage.problems} />}
      {(stage.state === 'previewed' || stage.state === 'importing') && (
        <Preview
          preview={stage.preview}
          schedule={schedule.name}
          busy={stage.state === 'importing'}
          // No Upload button until a preview has come back clean, and none
          // while one is in flight. A refused preview offers nothing to press.
          onApply={stage.state === 'previewed' && !stage.preview.problems.length ? () => runImport(stage.preview) : null}
        />
      )}
      {stage.state === 'done' && <Done result={stage.result} onAgain={startOver} />}

      <FormatHelp />
    </>
  );
}

// ---------------------------------------------------------------------------
// The two switches
// ---------------------------------------------------------------------------

/**
 * Merge and replace are not two settings of one knob; they are two different
 * questions, and the asymmetry is the whole point. Merging when you meant to
 * replace leaves a stale row somebody notices. Replacing when you meant to
 * merge deletes the rest of the year.
 */
function Choices({ choices, onChange, locked }: { choices: ImportChoices; onChange: (c: ImportChoices) => void; locked: boolean }) {
  return (
    <div className="importchoices">
      <fieldset disabled={locked}>
        <legend>What this file speaks for</legend>
        <label className="field check">
          <input type="radio" name="mode" checked={choices.mode === 'merge'} onChange={() => onChange({ ...choices, mode: 'merge' })} />
          <strong>Only what is in it.</strong>{' '}
          <span className="muted">
            A resident-block the file does not mention is left exactly as it is. This is how you upload a correction for two
            residents without touching the other eighty.
          </span>
        </label>
        <label className="field check">
          <input type="radio" name="mode" checked={choices.mode === 'replace'} onChange={() => onChange({ ...choices, mode: 'replace' })} />
          <strong>The whole year.</strong>{' '}
          <span className="muted">
            Anything already here and not in the file is removed, and residents the file leaves out come off the roster. For
            loading a year, or replacing one wholesale.
          </span>
        </label>
        <label className="field check">
          <input type="checkbox" checked={choices.createUnknown} onChange={(e) => onChange({ ...choices, createUnknown: e.target.checked })} />
          <strong>Accept rotation names I don't know yet.</strong>{' '}
          <span className="muted">
            Off, a name nobody recognises refuses the upload — which is usually right, because "Cardio" and "Cards" being two
            rotations is how a resident's ACGME months come out wrong. On, they are created and left for the program to
            classify.
          </span>
        </label>
      </fieldset>
    </div>
  );
}

// ---------------------------------------------------------------------------
// What would happen
// ---------------------------------------------------------------------------

function Preview({ preview, schedule, busy, onApply }: { preview: ImportPreview; schedule: string; busy: boolean; onApply: (() => void) | null }) {
  const { plan, diff, problems, warnings, source } = preview;
  const a = diff.assignments;
  const replacing = preview.mode === 'replace';

  return (
    <div className="card">
      <h2>
        What this would do
        <span className="muted">{problems.length ? 'refused' : 'nothing written yet'}</span>
      </h2>

      <ul className="orgstats">
        <li><strong>{plan.residents}</strong> residents</li>
        <li><strong>{plan.blocks}</strong> blocks</li>
        <li><strong>{plan.assignments.toLocaleString()}</strong> resident-blocks</li>
        <li><strong>{plan.rotations.length}</strong> rotations</li>
        <li><strong>{plan.academicYear || '—'}</strong> academic year</li>
      </ul>

      <SourceNotes source={source} />

      {problems.length > 0 && (
        <>
          <p className="error">
            <strong>Refused.</strong> Nothing has been written, and nothing will be until every one of these is fixed in the
            file and it is checked again.
          </p>
          <ProblemList problems={problems} />
        </>
      )}

      {!problems.length && (
        <>
          <h3>Against what is already in “{schedule}”</h3>
          <ul className="orgstats">
            <li><strong>{a.created.toLocaleString()}</strong> assignments added</li>
            <li><strong>{a.changed.toLocaleString()}</strong> changed</li>
            <li><strong>{a.removed.toLocaleString()}</strong> removed</li>
            <li><strong>{a.unchanged.toLocaleString()}</strong> unchanged</li>
          </ul>
          <ul className="orgstats">
            <li><strong>{diff.newResidents}</strong> residents new to this schedule</li>
            <li><strong>{diff.newBlocks}</strong> blocks created</li>
            <li><strong>{diff.newRotations}</strong> rotations created</li>
            {replacing && <li><strong>{diff.residentsLeavingTheRoster}</strong> residents leaving the roster</li>}
          </ul>

          {replacing && (diff.residentsLeavingTheRoster > 0 || a.removed > 0) && (
            <p className="notice">
              <strong>This removes things.</strong>{' '}
              {a.removed > 0 && `${a.removed.toLocaleString()} resident-block${a.removed === 1 ? '' : 's'} already here would be deleted`}
              {a.removed > 0 && diff.residentsLeavingTheRoster > 0 && ', and '}
              {diff.residentsLeavingTheRoster > 0 &&
                `${diff.residentsLeavingTheRoster} resident${diff.residentsLeavingTheRoster === 1 ? '' : 's'} would come off the roster`}
              . If that is not what you meant, switch to “only what is in it” above and check again.
            </p>
          )}

          {a.created === 0 && a.changed === 0 && a.removed === 0 && (
            <p className="muted">
              Nothing would change — every one of these {plan.assignments.toLocaleString()} resident-blocks is already here,
              exactly as the file has it.
            </p>
          )}
        </>
      )}

      {warnings.length > 0 && (
        <>
          <h3>Worth looking at</h3>
          <p className="muted small">Not enough to refuse the upload. True of the file, and possibly on purpose.</p>
          <ProblemList problems={warnings} kind="warn" />
        </>
      )}

      {plan.unknownLabels.length > 0 && (
        <>
          <h3>Rotation names this does not know</h3>
          <ul className="orgschedules">
            {plan.unknownLabels.map((u) => (
              <li key={u.label}>
                <span className="orgname">“{u.label}”</span>
                <span className="muted small">
                  {u.count} resident-block{u.count === 1 ? '' : 's'}
                  {u.residents.length ? ` · ${u.residents.join(', ')}${u.count > u.residents.length ? ' and others' : ''}` : ''}
                </span>
              </li>
            ))}
          </ul>
          <p className="small muted">
            Either it is a spelling of a rotation you already have — fix it in the file and it will be recognised — or it is a
            rotation this platform has not met, in which case tick “accept rotation names I don't know yet” above.
          </p>
        </>
      )}

      {onApply && (
        <p className="formactions">
          <button className="primary" type="button" disabled={busy} onClick={onApply}>
            {busy ? 'Uploading…' : replacing ? 'Replace the year with this file' : 'Upload these changes'}
          </button>{' '}
          <span className="muted small">This is the point at which something is written.</span>
        </p>
      )}
    </div>
  );
}

function Done({ result, onAgain }: { result: ImportResult; onAgain: () => void }) {
  const a = result.assignments;
  return (
    <div className="card">
      <h2>Uploaded</h2>
      <p>
        {result.academicYear} is in. {result.residents} residents, {result.blocks} blocks, {result.rotations} rotations.
      </p>
      <ul className="orgstats">
        <li><strong>{a.created.toLocaleString()}</strong> added</li>
        <li><strong>{a.changed.toLocaleString()}</strong> changed</li>
        <li><strong>{a.removed.toLocaleString()}</strong> removed</li>
        <li><strong>{a.unchanged.toLocaleString()}</strong> unchanged</li>
      </ul>
      {result.warnings.length > 0 && <ProblemList problems={result.warnings} kind="warn" />}
      <p>
        <button className="linklike" type="button" onClick={onAgain}>Upload another file</button>
      </p>
    </div>
  );
}

/** A file the server would not read at all, or an import it refused. Same shape either way. */
function Refused({ message, problems }: { message: string; problems: string[] }) {
  return (
    <div className="card">
      <h2>Nothing was written</h2>
      <p className="error">{message}</p>
      {problems.length > 0 ? (
        <ProblemList problems={problems} />
      ) : (
        <p className="muted small">
          If that message is not something you can act on, it is worth saying so — a refusal a program cannot fix is a bug in
          this screen rather than in the file.
        </p>
      )}
    </div>
  );
}

function ProblemList({ problems, kind = 'error' }: { problems: string[]; kind?: 'error' | 'warn' }) {
  // Long lists are scrollable rather than truncated: the twentieth line is
  // somebody's resident, and a screen that hides it sends them back to a
  // terminal to find out who.
  return (
    <ul className={`problems ${kind}`}>
      {problems.map((p, i) => (
        <li key={`${i}-${p.slice(0, 24)}`}>{p}</li>
      ))}
    </ul>
  );
}

/** How the server read the file — the readings it made on the program's behalf, before anything is applied. */
function SourceNotes({ source }: { source: { format: string; notes: string[] } }) {
  if (!source?.notes?.length) return null;
  return (
    <ul className="problems note">
      {source.notes.map((n, i) => (
        <li key={i}>{n}</li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// What a file may look like
// ---------------------------------------------------------------------------

/**
 * Printed from the server's own column table, so this cannot describe a
 * spelling the reader does not accept.
 */
function FormatHelp() {
  const [format, setFormat] = useState<ImportFormat | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    api.importFormat().then((f) => live && setFormat(f)).catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const needed = (format?.columns ?? []).filter((c) => c.required === 'always');
  const naming = (format?.columns ?? []).filter((c) => c.required === 'identity');
  const optional = (format?.columns ?? []).filter((c) => c.required === 'optional');

  return (
    <div className="card">
      <h2>
        What a file looks like
        <button className="linklike" type="button" onClick={() => setOpen(!open)}>{open ? 'hide' : 'show'}</button>
      </h2>
      <p className="muted small">
        A spreadsheet saved as CSV, one row per resident per block — or a schedule file this platform wrote itself. Commas,
        tabs and semicolons all work; the heading row is what matters, not the order of the columns.
      </p>
      {open && (
        <>
          <pre className="sample">{SAMPLE}</pre>
          {!format ? (
            <p className="muted">Loading the accepted headings…</p>
          ) : (
            <>
              <Columns title="Every file needs these" columns={needed} />
              <Columns title="And at least one of these, to say who each row is" columns={naming} />
              <Columns title="Read if they are there" columns={optional} />
              <p className="small muted">
                A year is {format.blocksPerYear} blocks. Dates are written <code>2026-07-01</code> and nothing else —{' '}
                <code>3/4/2026</code> is two different days depending on who typed it, and a block boundary is not a thing to
                be wrong about quietly, so it is refused rather than guessed at.
              </p>
              <p className="small muted">
                One thing this cannot read: a <strong>block grid</strong>, with residents down the side and blocks across the
                top. A grid does not carry the dates each block runs between, and the import needs them.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Columns({ title, columns }: { title: string; columns: ImportColumn[] }) {
  if (!columns.length) return null;
  return (
    <>
      <h3>{title}</h3>
      <ul className="orgschedules">
        {columns.map((c) => (
          <li key={c.field}>
            <span className="orgname">{c.label}</span>
            <span className="muted small">{c.about}</span>
            <span className="muted small" style={{ marginLeft: 'auto', textAlign: 'right' }}>
              also read as: {c.names.filter((n) => n !== c.label.toLowerCase()).slice(0, 4).join(', ') || '—'}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

const SAMPLE = `Resident,Last name,First name,PGY,Track,Block,Start,End,Rotation
R1,Alvarez,Nina,1,categorical,1,2026-07-01,2026-07-12,GP2
R1,Alvarez,Nina,1,categorical,2,2026-07-13,2026-07-26,Pulm
R2,Bell,Omar,3,prelim,1,2026-07-01,2026-07-12,GP3`;

const sizeOf = (text: string) => {
  const kb = new Blob([text]).size / 1024;
  return kb < 1024 ? `${Math.max(1, Math.round(kb))} KB` : `${(kb / 1024).toFixed(1)} MB`;
};
